import { defineComponent as _defineComponent } from 'vue'
import moment from "moment-timezone";
import PayoutInputHintBlock from "@/components/forms/payout/PayoutInputHintBlock.vue";
import ErrorMessageBlock from "@/components/forms/payout/ErrorMessageBlock.vue";
import FilteredInputWrapper from "@/components/forms/payout/forms/inputs/FilteredInputWrapper.vue";
import {computed, getCurrentInstance, onMounted, reactive, ref, watch} from "vue";
import {WebFormModel} from "capitalist-shared/src/core/model";
import RegularFormNumberedItemHeading
  from "@/views/pages/profile/RegularFormNumberedItemHeading.vue";
import MultipleDocumentFilesTableUpload
  from "@/components/forms/files/MultipleDocumentFilesTableUpload.vue";
import HorizontalFormGroup from "@/components/forms/horizontal/HorizontalFormGroup.vue";
import DropdownKeyvalue from "@/components/forms/dropdown/DropdownKeyValue.vue";
import {RemoteRequestState} from "capitalist-shared/src/core/remote";
import VueI18n from "vue-i18n";
import PayoutDateInput from "@/components/forms/payout/forms/PayoutDateInput.vue";
import CapInputSwitchbox from "@/components/forms/cap-input-switchbox.vue";
import AddressVerificationDocuments from "@/views/pages/profile/AddressVerificationDocuments.vue";

import {scrollToFormError} from "@/core/scroll";
import ModelValuesAutoSaver from "@/views/pages/profile/ModelValuesAutoSaver.vue";
import IdDocumentsRequirements from "@/views/pages/profile/IdDocumentsRequirements.vue";
import AddressDocumentsRequirements from "@/views/pages/profile/AddressDocumentsRequirements.vue";
import LegalDocumentsRequirements from "@/views/pages/profile/LegalDocumentsRequirements.vue";
import PayoutHintPanel from "@/components/forms/payout/PayoutHintPanel.vue";
import httpAxiosApi from "capitalist-shared/src/core/axios.errors.handle";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileUpdateLegal',
  emits: ['submit', 'check-profile'],
  setup(__props, { expose, emit: emits }) {

const activityOptionsBusiness = [
  'Рекламодатель (СНГ)',
  'Рекламодатель (МИР)',
  'Партнерская программа',
  'Рекламная сеть',
  'Криптобиржа',
  'Криптообменник',
  'Образование',
  'Маркетинговое агентство',
  'Беттинг',
  'Казино',
  'Форекс',
];

class ProfileUpdateLegalModel extends WebFormModel {
  loadState = new RemoteRequestState();
  submitState = new RemoteRequestState();

  countries = []

  values = {
    contact_telegram: '',
    contact_skype: '',
    contact_phone: '',
    contact_website: '',


    name: '',
    files: [],
    directorIdFiles: [],
    directorAddressFiles: [],
    country: '',
    bankCountry: '',
    address: '',
    number: '',
    activity: '',

    bankAccount: '',
    bankName: '',
    bankAddress: '',
    bankSwift: '',

    lastName: '',
    firstName: '',
    middleName: '',
    birthDay: '',
    phone: '',
    documentType: '',
    idNumber: '',
    idType: '',
    documentValidFrom: '',
    documentValidTo: '',
    documentFile: [],

    raAddressLine: '',
    raCity: '',
    raCountry: '',
    raZip: '',
    raState: '',
    sameAddress: true,
    paAddressLine: '',
    paCity: '',
    paCountry: '',
    paZip: '',
    paState: '',

    addressDocument: []
  }

  loadModel() {
    this.loadState.loadStart();
    return httpAxiosApi.get('/webapi/profile/update/legalModel')
      .then((res) => {
        this.options = res.data;
        this.countries = [...res.data.countries];
        this.loadState.loadEnd();
      })
      .catch((err) => {
        this.loadState.loadError();
        throw err;
      })
  }

  submit() {
    this.submitState.loadStart();
    this.errors.clear();
    return httpAxiosApi.post('/webapi/profile/update/legal', {
        m: {...this.values, companyDocuments: this.values.files.map((file) => file.id)},
      })
      .then((res) => {
        this.submitState.loadEnd();
        return res.data;
      })
      .catch((err) => {
        this.checkAxiosError(err);
        this.submitState.loadError();
        throw err;
      })
  }
}

const model = reactive(new ProfileUpdateLegalModel())


const idTypeOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  const values = {
    ['DOMESTIC_PASSPORT']: 'Внутренний паспорт',
    ['INTERNATIONAL_PASSPORT']: 'Заграничный паспорт',
    ['DRIVING_LICENSE']: 'Водительское удостоверение',
  }

  const result = {};

  Object.keys(values).forEach((item) => {
    result[item] = i18n ? i18n.t(values[item]) : values[item];
  });

  return result;
})


const countryOptions = computed(() => {
  const result = {};
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;
  const lang = String(i18n ? i18n.locale : 'ru') == 'ru' ? 'Ru' : 'En';

  if (model.loadState.isLoaded && model.countries.length > 0) {
    model.countries.forEach((country) => {
      result[country.alpha2] = country[`name${lang}`];
    });
  }
  return result;
});

const addressDocuments = ref(null);
const companyFilesSelector = ref(null);
const directorFilesSelector = ref(null);

const isFormDisabled = computed(() => {
  return model.submitState.isLoading;
})

const submitForm = () => {
  model.submit().then(() => {
    emits('submit');
    dataSaver.value?.clear();
  }).catch((error) => {
    scrollToFormError();

    if (error && (typeof error === 'object')
      && error.response
      && error.response?.status) {
      if (error.response?.status == 422) {
        if (error.response.data?.model.hasActiveRequest || error.response.data?.model.alreadyVerified) {
          emits('check-profile');
        }
      }
    }
  })
}

const isFileUploading = computed(() => {
  return model.loadState.isLoading
    || addressDocuments.value?.busy
    || companyFilesSelector.value?.busy
    || directorFilesSelector.value?.busy;
})

watch(() => [
  model.values.raCountry,
  model.values.raCity,
  model.values.raAddressLine,
  model.values.raZip,
  model.values.raState,
  model.values.sameAddress
].join(':'), (v) => {
  if (model.values.sameAddress) {
    model.values.paCountry = model.values.raCountry;
    model.values.paCity = model.values.raCity;
    model.values.paAddressLine = model.values.raAddressLine;
    model.values.paZip = model.values.raZip;
    model.values.paState = model.values.raState;
  }
});


const activityOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  const list = [
    'Платежные системы',
    'Партнерские программы',
    'Сервисы для вебмастеров',
    'Сервисы обмена валют',
    'Стартапы',
    'Рекламные сети',
    'Прочее'
  ];

  const result = {};

  list.forEach((item) => {
    result[item] = i18n ? i18n.t(item) : item;
  });

  return result;
});

const datePickerOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return {
    range: false,
    dateFormat: 'dd.MM.yyyy',
    minDate: moment().subtract(100, 'years').toDate(),
    maxDate: moment().subtract(18, 'years').toDate(),
    view: 'years',
    //minView: 'months',
    autoClose: true,
    language: i18n ? i18n.locale : 'ru'
    //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
  }
})

const onAddressDocumentsChanged = (docs) => {
  // console.log('address docs changed', docs)
  model.values.addressDocument = [...docs]
}

const idDocumentDatePickerOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return {
    range: false,
    dateFormat: 'dd.MM.yyyy',
    minDate: moment().subtract(100, 'years').toDate(),
    maxDate: moment().toDate(),
    view: 'days',
    //minView: 'months',
    autoClose: true,
    language: i18n ? i18n.locale : 'ru'
    //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
  }
})

const idDocumentEndDatePickerOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return {
    range: false,
    dateFormat: 'dd.MM.yyyy',
    minDate: moment().toDate(),
    maxDate: moment().add(30, 'years').toDate(),
    view: 'days',
    //minView: 'months',
    autoClose: true,
    language: i18n ? i18n.locale : 'ru'
    //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
  }
})

onMounted(() => {
  model.loadModel();
});

const busy = computed(() => {
  return model.loadState.isLoading || model.submitState.isLoading
    || isFileUploading.value || isFormDisabled.value;
})

const hideForm = () => {
  model.errors.clear();
}

const dataSaver = ref(null);
const birthDay = ref(null);
const documentValidFrom = ref(null);
const documentValidTo = ref(null);

const activateForm = () => {
  dataSaver.value?.touch();
}

const onSavedDataLoaded = (data) => {
  model.values = {...model.values, ...data};
}

const requiredTelegram = computed(() => {
  return !model.values.contact_phone;
})

const requiredPhone = computed(() => {
  return !model.values.contact_telegram;
})


expose({busy, hideForm, activateForm})

return { __sfc: true,activityOptionsBusiness, ProfileUpdateLegalModel, model, emits, idTypeOptions, countryOptions, addressDocuments, companyFilesSelector, directorFilesSelector, isFormDisabled, submitForm, isFileUploading, activityOptions, datePickerOptions, onAddressDocumentsChanged, idDocumentDatePickerOptions, idDocumentEndDatePickerOptions, busy, hideForm, dataSaver, birthDay, documentValidFrom, documentValidTo, activateForm, onSavedDataLoaded, requiredTelegram, requiredPhone, PayoutInputHintBlock, ErrorMessageBlock, FilteredInputWrapper, RegularFormNumberedItemHeading, MultipleDocumentFilesTableUpload, HorizontalFormGroup, DropdownKeyvalue, PayoutDateInput, CapInputSwitchbox, AddressVerificationDocuments, ModelValuesAutoSaver, IdDocumentsRequirements, AddressDocumentsRequirements, LegalDocumentsRequirements, PayoutHintPanel }
}

})